/**
 * Shared CSS drop-shadow filter for CanvasEditor + Shadow prep preview.
 * Accepts store shape ({ x, y }) or canvas shape ({ offsetX, offsetY }).
 */
export function hexToRgba(hex, alpha) {
  const raw = typeof hex === "string" ? hex.trim() : "#000000";
  const normalized = raw.startsWith("#") ? raw.slice(1) : raw;
  const full =
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => c + c)
          .join("")
      : normalized.padEnd(6, "0").slice(0, 6);
  const r = parseInt(full.slice(0, 2), 16) || 0;
  const g = parseInt(full.slice(2, 4), 16) || 0;
  const b = parseInt(full.slice(4, 6), 16) || 0;
  const a = Number.isFinite(Number(alpha)) ? Number(alpha) : 1;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export function buildDropShadowFilter(shadow = {}) {
  if (!shadow?.enabled) {
    return "none";
  }

  const offsetX = Math.round(Number(shadow.offsetX ?? shadow.x) || 0);
  const offsetY = Math.round(Number(shadow.offsetY ?? shadow.y) || 0);
  const blur = Math.round(Number(shadow.blur) || 0);
  const shadowColor = hexToRgba(shadow.color || "#000000", shadow.opacity ?? 1);

  return `drop-shadow(${offsetX}px ${offsetY}px ${blur}px ${shadowColor})`;
}

/** Padding so baked/exported shadows are not clipped at image edges. */
export function getShadowBleedPx(shadow = {}) {
  if (!shadow?.enabled) return 0;
  const offsetX = Math.abs(Number(shadow.offsetX ?? shadow.x) || 0);
  const offsetY = Math.abs(Number(shadow.offsetY ?? shadow.y) || 0);
  const blur = Math.max(0, Number(shadow.blur) || 0);
  return Math.ceil(blur * 2 + Math.max(offsetX, offsetY) + 4);
}

/** Scale shadow offsets/blur when the subject is drawn smaller than its natural size. */
export function scaleShadowForDisplay(shadow = {}, displayScale = 1) {
  if (!shadow?.enabled) return shadow;
  const scale = Number.isFinite(Number(displayScale))
    ? Math.max(0, Number(displayScale))
    : 1;
  if (scale === 1) return shadow;

  return {
    ...shadow,
    x: Math.round((Number(shadow.x ?? shadow.offsetX) || 0) * scale),
    y: Math.round((Number(shadow.y ?? shadow.offsetY) || 0) * scale),
    blur: Math.round((Number(shadow.blur) || 0) * scale),
  };
}
